<template>
  <div class="cash-register-wrapper">
    <div class="modal-backdrop fade show" v-if="isModalOpen"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" v-if="isModalOpen">
      <div class="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable modal-fullscreen-sm-down cash-register-dialog" role="document">
        <div class="modal-content shadow-lg border-0 rounded-4">
          <!-- Header -->
          <div class="modal-header border-0 pb-0 pt-4 px-4">
            <div class="d-flex align-items-center w-100">
              <div class="icon-circle me-3" :class="registerStatus === 'closed' ? 'bg-success-light' : 'bg-danger-light'">
                <AppIcon :name="registerStatus === 'closed' ? 'bi bi-door-open' : 'bi bi-door-closed'" :class="registerStatus === 'closed' ? 'bi bi-door-open' : 'bi bi-door-closed'" />
              </div>
              <div>
                <h5 class="modal-title fw-bold mb-0 text-dark">
                  {{ registerStatus === 'closed' ? $t('open_cash_register') : $t('close_cash_register') }}
                </h5>
                <p class="text-muted small mb-0">{{ registerStatus === 'closed' ? $t('start_new_session') : $t('end_current_session') }}</p>
              </div>
              <button type="button" class="btn-close ms-auto modal-close-button" @click="modalClosed"></button>
            </div>
          </div>

          <div class="modal-body px-4 pt-4 pb-3">
            <!-- Opening Register Form -->
            <transition name="fade-slide" mode="out-in">
              <div v-if="registerStatus === 'closed'" key="opening-form">
                <div class="info-card mb-4">
                  <div class="d-flex align-items-center">
                    <div class="info-icon me-3">
                      <AppIcon name="bi-info-circle-fill" class="bi bi-info-circle-fill" />
                    </div>
                    <div>
                      <p class="mb-0 small fw-medium text-primary">
                        {{ $t('opening_register_notice') || 'Opening a cash register will track all sales until it\'s closed.' }}
                      </p>
                      <p class="mb-0 extra-small text-muted">
                        {{ $t('ensure_starting_cash_accuracy') || 'Make sure to count your starting cash accurately.' }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="row g-4">
                  <div class="col-md-6">
                    <div class="form-group-premium">
                      <label for="opening_balance" class="label-premium">{{$t('opening_balance')}}</label>
                      <div class="input-group-premium">
                        <span class="currency-addon">{{ business.currency }}</span>
                        <input 
                          type="number" 
                          class="input-premium" 
                          id="opening_balance" 
                          v-model="register.opening_balance"
                          step="0.01"
                          @focus="handleInputFocus($event, 'opening_balance')"
                          @blur="handleInputBlur('opening_balance')"
                        >
                      </div>
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <div class="form-group-premium">
                      <label class="label-premium">{{$t('cashier')}}</label>
                      <div class="input-group-premium disabled">
                        <span class="icon-addon"><AppIcon name="bi-person" class="bi bi-person" /></span>
                        <input 
                          type="text" 
                          class="input-premium" 
                          :value="user.name"
                          disabled
                        >
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Quick Opening Balances -->
                <div class="mt-4">
                  <label class="label-premium mb-2">{{ $t('quick_amounts') || 'Quick Amounts' }}</label>
                  <div class="quick-amounts-grid">
                    <button v-for="amount in [0, 50, 100, 200, 500]" :key="amount" 
                      type="button"
                      class="quick-chip" 
                      @click="register.opening_balance = amount">
                      {{ amount }} {{ business.currency }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Closing Register Form -->
              <div v-else key="closing-form">
                <!-- Session Header Info -->
                <div class="session-summary-card mb-4">
                  <div class="row g-3">
                    <div class="col-6 col-md-3">
                      <div class="summary-item">
                        <span class="summary-label">{{$t('opened_at')}}</span>
                        <span class="summary-value">{{ formatTime(register.opened_at) }}</span>
                      </div>
                    </div>
                    <div class="col-6 col-md-3">
                      <div class="summary-item">
                        <span class="summary-label">{{$t('duration')}}</span>
                        <span class="summary-value">{{ calculateSessionDuration() }}</span>
                      </div>
                    </div>
                    <div class="col-6 col-md-3">
                      <div class="summary-item">
                        <span class="summary-label">{{$t('opening')}}</span>
                        <span class="summary-value text-primary">{{ formatMoney(register.opening_balance) }}</span>
                      </div>
                    </div>
                    <div class="col-6 col-md-3">
                      <div class="summary-item">
                        <span class="summary-label">{{$t('transactions')}}</span>
                        <span class="summary-value">{{ transactionCount }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row g-4">
                  <!-- Expected & Actual Balance -->
                  <div class="col-md-6">
                    <div class="form-group-premium">
                      <label for="closing_balance" class="label-premium">{{$t('actual_cash_amount') || 'Actual Cash in Drawer'}}</label>
                      <div class="input-group-premium" :class="{'border-danger': balanceDifference < 0, 'border-success': balanceDifference > 0}">
                        <span class="currency-addon">{{ business.currency }}</span>
                        <input 
                          type="number" 
                          class="input-premium" 
                          id="closing_balance" 
                          v-model="register.closing_balance"
                          step="0.01"
                          @focus="handleInputFocus($event, 'closing_balance')"
                          @blur="handleInputBlur('closing_balance')"
                        >
                      </div>
                      <small class="text-muted mt-1 d-block">{{$t('count_cash_carefully') || 'Count your physical cash.'}}</small>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-group-premium">
                      <label class="label-premium">{{$t('expected_balance')}}</label>
                      <div class="input-group-premium disabled">
                        <span class="currency-addon">{{ business.currency }}</span>
                        <input 
                          type="number" 
                          class="input-premium" 
                          :value="expectedBalance.toFixed(2)"
                          disabled
                        >
                      </div>
                      <small class="text-muted mt-1 d-block">{{$t('opening')}} + {{$t('total_sales')}}</small>
                    </div>
                  </div>
                </div>

                <!-- Difference Alert -->
                <div v-if="register.closing_balance !== ''" class="mt-4">
                    <div class="difference-card" :class="balanceStatusClass">
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="d-flex align-items-center">
                        <div class="diff-icon me-3">
                          <AppIcon :name="balanceStatusIcon" :class="balanceStatusIcon" />
                        </div>
                        <div>
                          <p class="mb-0 fw-bold">{{ balanceStatusLabel }}</p>
                          <p class="mb-0 extra-small opacity-75">{{ $t('balance_difference') }}</p>
                        </div>
                      </div>
                      <div class="text-end">
                        <p class="mb-0 fs-4 fw-bold">{{ formatMoney(Math.abs(balanceDifference)) }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Sales Summary -->
                <div class="mt-4">
                  <label class="label-premium mb-2">{{$t('sales_summary')}}</label>
                  <div class="sales-details-grid">
                    <div class="sales-detail-item">
                      <span class="detail-label">{{$t('total_sales')}}</span>
                      <span class="detail-value">{{ formatMoney(totalSales) }}</span>
                    </div>
                    <div class="sales-detail-item">
                      <span class="detail-label">{{$t('avg_sale')}}</span>
                      <span class="detail-value">
                        {{ transactionCount > 0 ? formatMoney((totalSales / transactionCount).toFixed(2)) : '0.00' }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Notes -->
                <div class="mt-4">
                  <collapsible-notes-field
                    id="sessionNotes"
                    v-model="register.notes"
                    rows="2"
                  />
                </div>
                
                <div class="mt-3">
                  <router-link :to="{ name: 'PosCashRegisterDetails', params: { id: register.id } }" class="btn btn-link p-0 text-decoration-none small">
                    <AppIcon name="bi-eye" class="bi bi-eye me-1" />{{$t('view_detailed_session_report') || 'View full session report'}}
                  </router-link>
                </div>
              </div>
            </transition>
          </div>

          <!-- Footer Actions -->
          <div class="modal-footer border-0 p-4 pt-0">
            <div class="w-100">
              <button 
                v-if="registerStatus === 'closed'"
                type="button" 
                class="btn btn-success btn-lg w-100 py-3 mb-2 rounded-3 shadow-sm action-btn" 
                @click="openRegister" 
                :disabled="loading"
              >
                <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                <AppIcon name="bi-check2-circle" v-else class="bi bi-check2-circle me-2" />
                {{$t('open_register')}}
              </button>
              <button 
                v-else
                type="button" 
                class="btn btn-danger btn-lg w-100 py-3 mb-2 rounded-3 shadow-sm action-btn" 
                @click="closeRegister" 
                :disabled="loading"
              >
                <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                <AppIcon name="bi-lock" v-else class="bi bi-lock me-2" />
                {{$t('close_register')}}
              </button>
              
              <button type="button" class="btn btn-light btn-lg w-100 py-3 rounded-3 border" @click="modalClosed">
                {{$t('cancel') || 'Cancel'}}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper';
import moment from 'moment';

export default {
  name: 'CashRegisterModal',
  props: {
    isModalOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      register: {
        id: null,
        opening_balance: 0,
        closing_balance: '',
        status: 'closed',
        opened_at: null,
        closed_at: null,
        cashier_id: null,
        notes: ''
      },
      totalSales: 0,
      transactionCount: 0,
      loading: false
    };
  },
  computed: {
    registerStatus() {
      return this.register.status;
    },
    business() { 
      return this.$store.getters.BUSINESS;
    },
    user() { 
      return this.$store.getters.USER;
    },
    expectedBalance() {
      return parseFloat(this.register.opening_balance || 0) + parseFloat(this.totalSales || 0);
    },
    balanceDifference() {
      if (this.register.closing_balance === '') return 0;
      return parseFloat(this.register.closing_balance) - this.expectedBalance;
    },
    balanceStatusLabel() {
      if (this.balanceDifference === 0) return this.$t('balanced');
      return this.balanceDifference < 0 ? this.$t('shortage') : this.$t('surplus');
    },
    balanceStatusClass() {
      if (this.balanceDifference === 0) return 'perfect';
      return this.balanceDifference < 0 ? 'shortage' : 'surplus';
    },
    balanceStatusIcon() {
      if (this.balanceDifference === 0) return 'bi bi-check-circle-fill';
      return this.balanceDifference < 0 ? 'bi bi-dash-circle-fill' : 'bi bi-plus-circle-fill';
    }
  },
  methods: {
    formatMoney(number) { return helper.formatMoney(number); },
    handleInputFocus(event, field) {
      if (this.register[field] === 0 || this.register[field] === '0' || this.register[field] === '0.00') {
        this.register[field] = '';
      } else {
        event.target.select();
      }
    },
    handleInputBlur(field) {
      if (this.register[field] === '' || this.register[field] === null) {
        this.register[field] = 0;
      }
    },
    formatTime(date) {
      if (!date) return '--:--';
      return moment(date).format('HH:mm (ddd)');
    },
    calculateSessionDuration() {
      if (!this.register.opened_at) return '--';
      const start = moment(this.register.opened_at);
      const end = moment();
      const duration = moment.duration(end.diff(start));
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      return `${hours}h ${minutes}m`;
    },
    async fetchCurrentRegister() {
      try {
        this.loading = true;
        const response = await helper.customGet(`${process.env.VUE_APP_API}/cash-register`);
        if (response.data && response.data.id) {
          this.register = { ...response.data, closing_balance: '' };
          await this.fetchTotalSales();
        } else {
          this.resetRegister();
        }
        this.loading = false;
      } catch (error) {
        this.resetRegister();
        this.loading = false;
      }
    },
    async fetchTotalSales() {
      if (this.register.status !== 'open') return;
      try {
        const response = await helper.customGet(
          `${process.env.VUE_APP_API}/pos/sales/summary?from=${this.register.opened_at}&cash_register_id=${this.register.id}`
        );
        this.totalSales = response?.data[0]?.total_sales || 0;
        this.transactionCount = response?.data[0]?.total_transactions || 0;
      } catch (error) {
        this.totalSales = 0;
        this.transactionCount = 0;
      }
    },
    resetRegister() {
      this.register = {
        opening_balance: 0,
        closing_balance: '',
        status: 'closed',
        opened_at: null,
        closed_at: null,
        cashier_id: null,
        notes: ''
      };
      this.totalSales = 0;
      this.transactionCount = 0;
    },
    async openRegister() {
      try {
        if (this.register.opening_balance === undefined || this.register.opening_balance < 0) {
          helper.toastMessage("Please enter a valid opening balance", "error");
          return;
        }
        
        this.loading = true;
        const payload = {
          opening_balance: parseFloat(this.register.opening_balance),
          status: 'open'
        };
        
        const response = await helper.customPost(`${process.env.VUE_APP_API}/cash-register`, payload, {showToast: true});
        
        if (response.data) {
          this.register = { ...response.data, closing_balance: '' };
          this.$emit('registerStatusChanged', 'open');
          this.modalClosed();
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },
    async closeRegister() {
      try {
        if (this.register.closing_balance === '') {
          helper.toastMessage("Please enter the closing balance", "error");
          return;
        }
        
        this.loading = true;
        const payload = {
          closing_balance: parseFloat(this.register.closing_balance),
          notes: this.register.notes,
          status: 'closed'
        };
        
        await helper.customPatch(`${process.env.VUE_APP_API}/cash-register/${this.register.id}`, payload, {showToast: true});
        
        this.loading = false;
        this.$emit('registerStatusChanged', 'closed');
        this.modalClosed();
        this.resetRegister();
      } catch (error) {
        this.loading = false;
      }
    },
    modalClosed() {
      this.$emit('modalClosed', true);
    }
  },
  watch: {
    isModalOpen(newVal) {
      if (newVal) {
        this.fetchCurrentRegister();
      }
    }
  },
  mounted() {
    if (this.isModalOpen) {
      this.fetchCurrentRegister();
    }
  }
};
</script>

<style scoped>
.cash-register-wrapper { position: relative; }
.modal-backdrop { z-index: 2040; background-color: rgba(0, 0, 0, 0.6); backdrop-filter: blur(4px); }
.modal { z-index: 2050; }

.cash-register-dialog {
  width: calc(100% - 2rem);
  max-width: 1100px;
}

.cash-register-dialog .modal-content {
  max-height: calc(100vh - 2rem);
}

.modal-close-button {
  width: 2.75rem;
  height: 2.75rem;
  padding: 0.75rem;
  border-radius: 0.75rem;
}

.icon-circle {
  width: 50px;
  height: 50px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.bg-success-light { background-color: rgba(25, 135, 84, 0.1); color: #198754; }
.bg-danger-light { background-color: rgba(220, 53, 69, 0.1); color: #dc3545; }

.info-card {
  background-color: rgba(13, 110, 253, 0.05);
  border-radius: 12px;
  padding: 1rem;
  border-left: 4px solid #0d6efd;
}

.info-icon {
  font-size: 1.25rem;
  color: #0d6efd;
}

.extra-small { font-size: 0.7rem; }

.form-group-premium { margin-bottom: 0.5rem; }
.label-premium {
  display: block;
  font-weight: 700;
  font-size: 0.75rem;
  text-uppercase: uppercase;
  color: #6c757d;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.input-group-premium {
  display: flex;
  background: #f8f9fa;
  border: 2px solid #f1f3f5;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.input-group-premium:focus-within {
  border-color: #0d6efd;
  background: white;
  box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.1);
}

.input-group-premium.disabled { background: #e9ecef; border-color: #dee2e6; }

.currency-addon, .icon-addon {
  padding: 0.75rem 1rem;
  background: rgba(0,0,0,0.02);
  border-right: 1px solid rgba(0,0,0,0.05);
  font-weight: 700;
  color: #495057;
  display: flex;
  align-items: center;
}

.input-premium {
  flex: 1;
  border: none;
  background: transparent;
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: #212529;
  font-size: 1.1rem;
}

.input-premium:focus { outline: none; }

.quick-amounts-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.75rem;
}

.quick-chip {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  min-height: 56px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
  transition: all 0.2s ease;
  cursor: pointer;
}

.quick-chip:hover {
  border-color: #0d6efd;
  color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.05);
}

.session-summary-card {
  background: #212529;
  border-radius: 16px;
  padding: 1.25rem;
  color: white;
}

.summary-item {
  display: flex;
  flex-direction: column;
}

.summary-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  opacity: 0.6;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.summary-value {
  font-weight: 700;
  font-size: 0.9rem;
}

.difference-card {
  padding: 1.25rem;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.difference-card.perfect { background-color: #d1e7dd; color: #0f5132; }
.difference-card.shortage { background-color: #f8d7da; color: #842029; }
.difference-card.surplus { background-color: #cfe2ff; color: #084298; }

.diff-icon { font-size: 1.5rem; }

.sales-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.sales-detail-item {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  border: 1px solid #e9ecef;
}

.detail-label { font-size: 0.7rem; color: #6c757d; font-weight: 600; margin-bottom: 0.25rem; }
.detail-value { font-weight: 700; font-size: 1.1rem; color: #212529; }

.form-control-premium {
  width: 100%;
  border: 2px solid #f1f3f5;
  border-radius: 12px;
  padding: 0.75rem;
  background: #f8f9fa;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.form-control-premium:focus {
  outline: none;
  border-color: #0d6efd;
  background: white;
  box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.1);
}

.action-btn {
  font-weight: 700;
  transition: all 0.3s ease;
}

.modal-footer .btn {
  min-height: 60px;
  font-size: 1.05rem;
  font-weight: 700;
}

.action-btn:hover {
  transform: translateY(-2px);
  filter: brightness(1.1);
}

/* Transitions */
.fade-slide-enter-active, .fade-slide-leave-active { transition: all 0.3s ease; }
.fade-slide-enter, .fade-slide-leave-to { opacity: 0; transform: translateY(-10px); }

@media (max-width: 576px) {
  .cash-register-dialog {
    width: 100%;
    max-width: none;
  }

  .modal-body { padding: 1.25rem !important; }

  .quick-amounts-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
